import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useBundleSuggestionsForDeal } from '@/hooks/useBundleEffectiveness';
import { useProductBundles, calculateBundleSavings } from '@/hooks/useProductBundles';
import { Sparkles, TrendingUp, Target, DollarSign, Zap } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

interface SmartBundleSuggestionsProps {
  dealAmount: number;
  onSelectBundle: (bundleId: string) => void;
}

export function SmartBundleSuggestions({ dealAmount, onSelectBundle }: SmartBundleSuggestionsProps) {
  const { data: suggestions, isLoading: suggestionsLoading } = useBundleSuggestionsForDeal(dealAmount);
  const { data: allBundles, isLoading: bundlesLoading } = useProductBundles();

  const isLoading = suggestionsLoading || bundlesLoading;

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-20" />)}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!suggestions || suggestions.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Smart Bundle Suggestions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-4">
            No bundle suggestions available for this deal size yet.
          </p>
        </CardContent>
      </Card>
    );
  }

  const getBundleDetails = (bundleId: string) => {
    return allBundles?.find(b => b.id === bundleId);
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-chart-1';
    if (score >= 60) return 'text-chart-2';
    if (score >= 40) return 'text-chart-3';
    return 'text-muted-foreground';
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          Smart Bundle Suggestions
        </CardTitle>
        <CardDescription className="text-xs">
          Based on win rates for ${dealAmount.toLocaleString()} deals
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {suggestions.slice(0, 3).map((suggestion, index) => {
          const bundle = getBundleDetails(suggestion.bundle_id);
          const savings = bundle ? calculateBundleSavings(bundle) : null;
          
          return (
            <div
              key={suggestion.bundle_id}
              className={`p-3 rounded-lg border transition-all hover:border-primary/30 ${
                index === 0 ? 'bg-primary/5 border-primary/20' : 'bg-muted/30'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    {index === 0 && (
                      <Badge variant="default" className="text-xs px-1.5 py-0">
                        <Zap className="h-3 w-3 mr-0.5" />
                        Top Pick
                      </Badge>
                    )}
                    <span className="font-medium text-sm">{suggestion.bundle_name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    ${suggestion.bundle_price.toLocaleString()}
                    {savings && savings.savingsPercent > 0 && (
                      <span className="text-chart-1 ml-1">
                        ({savings.savingsPercent.toFixed(0)}% off)
                      </span>
                    )}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant={index === 0 ? 'default' : 'outline'}
                  onClick={() => onSelectBundle(suggestion.bundle_id)}
                  className="text-xs h-7"
                >
                  Add
                </Button>
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="flex items-center gap-1">
                  <TrendingUp className="h-3 w-3 text-muted-foreground" />
                  <span className={getScoreColor(suggestion.conversion_rate)}>
                    {suggestion.conversion_rate.toFixed(0)}% win
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Target className="h-3 w-3 text-muted-foreground" />
                  <span>{suggestion.size_fit_percent}% fit</span>
                </div>
                <div className="flex items-center gap-1">
                  <DollarSign className="h-3 w-3 text-muted-foreground" />
                  <span>${(suggestion.total_revenue / 1000).toFixed(0)}k won</span>
                </div>
              </div>

              {/* Effectiveness Score Bar */}
              <div className="mt-2">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Effectiveness</span>
                  <span className={getScoreColor(suggestion.effectiveness_score)}>
                    {suggestion.effectiveness_score}/100
                  </span>
                </div>
                <Progress value={suggestion.effectiveness_score} className="h-1.5" />
              </div>
            </div>
          );
        })}

        {suggestions.length > 3 && (
          <p className="text-xs text-muted-foreground text-center pt-1">
            +{suggestions.length - 3} more suggestions available
          </p>
        )}
      </CardContent>
    </Card>
  );
}
